<template>
  <v-form v-model="searchParamsValid" class="mx-3 mt-2">
    <!-- Common search parameters -->
    <v-row>
      <v-col cols="12" sm="5" class="pl-0 py-0">
        <v-text-field
          v-model="updatedSearchParams.carNumber"
          label="Autonumero"
          clearable
          hide-details
        ></v-text-field>
        <v-text-field
          v-model="updatedSearchParams.serviceNumber"
          label="Palvelutunnus"
          clearable
          hide-details
        ></v-text-field>
      </v-col>
      <v-col
        cols="1"
        sm="0"
        class="d-flex align-center justify-center py-0">
        <v-divider inset vertical></v-divider>
      </v-col>
      <v-col cols="12" sm="5" class="pl-0 py-0">
        <v-text-field
          v-model="updatedSearchParams.orderId"
          label="Tilaustunnus"
          clearable
          hide-details
          @input="updatedSearchParams.orderId = (updatedSearchParams.orderId) ? updatedSearchParams.orderId.toUpperCase() : updatedSearchParams.orderId"
        ></v-text-field>
        <v-text-field
          v-model="updatedSearchParams.receiptNumber"
          label="Kuittinumero"
          clearable
          hide-details
        ></v-text-field>
      </v-col>
    </v-row>

    <!-- Validation & Invoice States -->
    <v-row align="center" class="mt-4">
      <v-col cols="6" sm="4" class="pl-0 py-0">
        <v-text-field
          v-model="updatedSearchParams.salesInvoiceNumbers"
          label="Myyntilaskun numero"
        ></v-text-field>
      </v-col>
      <v-col cols="6" sm="4" class="pl-0 py-0">
        <v-text-field
          v-model="updatedSearchParams.purchaseInvoiceNumbers"
          label="Ostolaskun numero"
        ></v-text-field>
      </v-col>
      <v-col cols="12" sm="4" class="pl-0 py-0">
        <v-btn
          @click="toggleFailedValidations"
          :outlined="!hasFailedValidationsActive"
          :color="hasFailedValidationsActive ? 'primary' : ''"
          small
          class="pt-0"
        >
          Virheessä olevat matkat
        </v-btn>
      </v-col>
    </v-row>

    <!-- Toggle Button for Invoice States -->
    <v-row>
      <v-col cols="12" class="pl-0 py-0">
        <v-btn
          text
          small
          @click="showInvoiceStates = !showInvoiceStates"
          class="ml-0"
        >
          <v-icon small>{{ showInvoiceStates ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
          {{ showInvoiceStates ? 'Piilota tilasuodattimet' : 'Näytä tilasuodattimet' }}
        </v-btn>
      </v-col>
    </v-row>

    <!-- Invoice States - Expandable -->
    <v-expand-transition>
      <div v-show="showInvoiceStates">
        <v-row>
          <v-col cols="12" sm="8">
            <ValidationStatesField
              :validationStates="updatedSearchParams.validationStates"
              @update:validationStates="newValues =>
                updatedSearchParams.validationStates = newValues">
            </ValidationStatesField>
            <v-slide-y-transition>
              <v-item-group v-if="updatedSearchParams.validationStates.length === 1 && updatedSearchParams.validationStates.includes(validationStatusTypes.New)">
                <v-row>
                  <v-item v-slot="{ active, toggle }">
                    <v-checkbox
                      :value="updatedSearchParams.missingTrip = active"
                      label="Tilaus puuttuu"
                      :ripple="false"
                      @click.prevent="toggle">
                    </v-checkbox>
                  </v-item>
                  <v-item v-slot="{ active, toggle }">
                    <v-checkbox
                    :value="updatedSearchParams.missingCharge = active"
                      label="Kuitti puuttuu"
                      :ripple="false"
                      @click.prevent="toggle"
                      class="ml-4">
                    </v-checkbox>
                  </v-item>
                  <v-spacer></v-spacer>
                </v-row>
              </v-item-group>
            </v-slide-y-transition>
          </v-col>
        </v-row>
        <v-row align="center">
          <v-col cols="12" sm="4">
            <SalesInvoiceStatesField
              :updatedSearchParams="updatedSearchParams.salesInvoiceStates"
              :disabled="!!updatedSearchParams.salesInvoiceNumbers"
              @update:updatedSearchParams="newValues =>
                updatedSearchParams.salesInvoiceStates = newValues">
            </SalesInvoiceStatesField>
          </v-col>
          <v-col cols="12" sm="4">
            <PurchaseInvoiceStatesField
              :updatedSearchParams="updatedSearchParams.purchaseInvoiceStates"
              :disabled="!!updatedSearchParams.purchaseInvoiceNumbers"
              @update:updatedSearchParams="newValues =>
                updatedSearchParams.purchaseInvoiceStates = newValues">
            </PurchaseInvoiceStatesField>
          </v-col>
          <v-col cols="12" sm="4">
            <ProvisionInvoiceStatesField
              :updatedSearchParams="updatedSearchParams.provisionInvoiceStates"
              @update:updatedSearchParams="newValues =>
                updatedSearchParams.provisionInvoiceStates = newValues">
            </ProvisionInvoiceStatesField>
          </v-col>
        </v-row>
      </div>
    </v-expand-transition>

    <!-- Date Ranges Section -->

    <!-- Trip Started - Always Visible -->
    <v-row align="center" class="mt-4">
      <v-col cols="12" md="2" class="pl-0 py-0">Matkan aloitus</v-col>
      <v-col class="pl-0 py-0" cols="12" sm="6" md="3">
        <v-text-field
          v-model="updatedSearchParams.tripStartedFrom"
          label="Alkaen"
          type="date"
          max="2099-12-31"
        ></v-text-field>
      </v-col>
      <v-col class="pl-0 py-0" cols="12" sm="6" md="3">
        <v-text-field
          v-model="updatedSearchParams.tripStartedTo"
          label="Päättyen"
          type="date"
          max="2099-12-31"
        ></v-text-field>
      </v-col>
      <v-col cols="12" sm="2" class="py-0">
        <v-btn text @click="clearDateRange('tripStarted')">{{$t('common.clear')}}</v-btn>
      </v-col>
    </v-row>

    <!-- Toggle Button for More Date Ranges -->
    <v-row>
      <v-col cols="12" class="pl-0 py-0">
        <v-btn
          text
          small
          @click="showMoreDateRanges = !showMoreDateRanges"
          class="ml-0"
        >
          <v-icon small>{{ showMoreDateRanges ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
          {{ showMoreDateRanges ? 'Piilota päivämääräsuodattimet' : 'Näytä lisää päivämääräsuodattimia' }}
        </v-btn>
      </v-col>
    </v-row>

    <!-- Additional Date Ranges - Expandable -->
    <v-expand-transition>
      <div v-show="showMoreDateRanges">
        <v-row align="center">
          <v-col cols="12" md="2" class="pl-0 py-0">Matkan lopetus</v-col>
          <v-col class="pl-0 py-0" cols="12" sm="6" md="3">
            <v-text-field
              v-model="updatedSearchParams.tripEndedFrom"
              label="Alkaen"
              type="date"
              max="2099-12-31"
            ></v-text-field>
          </v-col>
          <v-col class="pl-0 py-0" cols="12" sm="6" md="3">
            <v-text-field
              v-model="updatedSearchParams.tripEndedTo"
              label="Päättyen"
              type="date"
              max="2099-12-31"
            ></v-text-field>
          </v-col>
          <v-col cols="12" sm="2" class="py-0">
            <v-btn text @click="clearDateRange('tripEnded')">{{$t('common.clear')}}</v-btn>
          </v-col>
        </v-row>
                <v-row align="center">
          <v-col cols="12" md="2" class="pl-0 py-0">Muokkausaika</v-col>
          <v-col class="pl-0 py-0" cols="12" sm="6" md="3">
            <v-text-field
              v-model="updatedSearchParams.modifiedFrom"
              label="Alkaen"
              type="date"
              max="2099-12-31"
            ></v-text-field>
          </v-col>
          <v-col class="pl-0 py-0" cols="12" sm="6" md="3">
            <v-text-field
            v-model="updatedSearchParams.modifiedTo"
            label="Päättyen"
            type="date"
            max="2099-12-31"
            ></v-text-field>
          </v-col>
          <v-col cols="12" sm="2" class="py-0">
            <v-btn text @click="clearDateRange('modified')">{{$t('common.clear')}}</v-btn>
          </v-col>
        </v-row>
      </div>
    </v-expand-transition>

    <!-- Financial Ranges Section -->

    <!-- Cost - Always Visible -->
    <v-row align="center" class="mt-4">
      <v-col cols="12" md="2" class="pl-0 py-0 mb-2">Kustannus</v-col>
      <v-col cols="6" sm="auto" class="py-0">
        <v-text-field
          v-model="updatedSearchParams.costFrom"
          single-line
          type="number"
          placeholder="-"
          variant="outlined"
          class="pt-0 centered-input"
          style="width: 70px"
          dense
        ></v-text-field>
      </v-col>
      <v-col sm="auto" class="hidden-xs-only py-0 pb-3" style="font-size: 1.5em;">
        -
      </v-col>
      <v-col cols="6" sm="auto" class="py-0">
        <v-text-field
          v-model="updatedSearchParams.costTo"
          single-line
          type="number"
          placeholder="-"
          variant="outlined"
          class="pt-0 centered-input"
          style="width: 70px"
          dense
        ></v-text-field>
      </v-col>
      <v-col cols="12" sm="2" class="py-0">
        <v-btn text @click="clearFinancialRange('cost')">{{$t('common.clear')}}</v-btn>
      </v-col>
      <v-spacer></v-spacer>
    </v-row>

    <!-- Toggle Button for More Financial Ranges -->
    <v-row>
      <v-col cols="12" class="pl-0 py-0">
        <v-btn
          text
          small
          @click="showMoreFinancialRanges = !showMoreFinancialRanges"
          class="ml-0">
          <v-icon small>{{ showMoreFinancialRanges ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
            {{ showMoreFinancialRanges ? 'Piilota hintasuodattimet' : 'Näytä lisää hintasuodattimia' }}
        </v-btn>
      </v-col>
    </v-row>

    <!-- More Financial Filters - Expandable -->
    <v-expand-transition>
      <div v-show="showMoreFinancialRanges">
        <v-row align="center">
          <v-col cols="12" md="2" class="pl-0 py-0 mb-2">Korvaus</v-col>
          <v-col cols="6" sm="auto" class="py-0">
            <v-text-field
              v-model="updatedSearchParams.reimbursementFrom"
              single-line
              type="number"
              placeholder="-"
              variant="outlined"
              class="pt-0 centered-input"
              style="width: 70px"
              dense
            ></v-text-field>
          </v-col>
          <v-col sm="auto" class="hidden-xs-only py-0 pb-3" style="font-size: 1.5em;">
            -
          </v-col>
          <v-col cols="6" sm="auto" class="py-0">
            <v-text-field
              v-model="updatedSearchParams.reimbursementTo"
              single-line
              type="number"
              placeholder="-"
              variant="outlined"
              class="pt-0 centered-input"
              style="width: 70px"
              dense
            ></v-text-field>
          </v-col>
          <v-col cols="12" sm="2" class="py-0">
            <v-btn text @click="clearFinancialRange('reimbursement')">{{$t('common.clear')}}</v-btn>
          </v-col>
          <v-spacer></v-spacer>
        </v-row>
        <v-row align="center">
          <v-col cols="12" md="2" class="pl-0 py-0 mb-2">Omavastuu</v-col>
          <v-col cols="6" sm="auto" class="py-0">
            <v-text-field
              v-model="updatedSearchParams.deductibleFrom"
              single-line
              type="number"
              placeholder="-"
              variant="outlined"
              class="pt-0 centered-input"
              style="width: 70px"
              dense
            ></v-text-field>
          </v-col>
          <v-col sm="auto" class="hidden-xs-only py-0 pb-3" style="font-size: 1.5em;">
            -
          </v-col>
          <v-col cols="6" sm="auto" class="py-0">
            <v-text-field
              v-model="updatedSearchParams.deductibleTo"
              single-line
              type="number"
              placeholder="-"
              variant="outlined"
              class="pt-0 centered-input"
              style="width: 70px"
              dense
            ></v-text-field>
          </v-col>
          <v-col cols="12" sm="2" class="py-0">
            <v-btn text @click="clearFinancialRange('deductible')">{{$t('common.clear')}}</v-btn>
          </v-col>
          <v-spacer></v-spacer>
        </v-row>
      </div>
    </v-expand-transition>

    <!-- Toggle Button for More Search Filters -->
    <v-row class="mt-4">
      <v-col cols="12" class="pl-0 py-0">
        <v-btn
          @click="showMoreSearchFilters = !showMoreSearchFilters"
          outlined
          block
        >
          <v-icon left>{{ showMoreSearchFilters ? 'mdi-filter-minus-outline' : 'mdi-filter-plus-outline' }}</v-icon>
          {{ showMoreSearchFilters ? 'Piilota hakusuodattimet' : 'Näytä lisää hakusuodattimia' }}
        </v-btn>
      </v-col>
    </v-row>

    <!-- More Search Filters - Expandable -->
    <v-expand-transition>
      <div v-show="showMoreSearchFilters">
        <v-row>
          <v-col cols="12" sm="5" class="pl-0 py-0">
            <v-text-field
              v-model="updatedSearchParams.ssn"
              label="Hetu"
              clearable
              hide-details
            ></v-text-field>
            <v-text-field
              v-model="updatedSearchParams.cardNumber"
              label="Kortin numero"
              clearable
              hide-details
            ></v-text-field>
            <v-text-field
              v-model="updatedSearchParams.taximeterSerialNumber"
              label="Taksamittarin sarjanumero"
              clearable
              hide-details
            ></v-text-field>
            <MeterTypesField
              :updatedSearchParams="updatedSearchParams.meterTypes"
              @update:updatedSearchParams="newValues =>
                updatedSearchParams.meterTypes = newValues"
            ></MeterTypesField>
            <v-text-field
              v-model="updatedSearchParams.operatorId"
              label="Operaattorin tunniste"
              clearable
              hide-details
            ></v-text-field>
            <v-text-field
              v-model="updatedSearchParams.referenceNumber"
              label="Ulkoisen järjestelmän viitenumero"
              clearable
              hide-details
            ></v-text-field>
          </v-col>
          <v-col
            cols="1"
            sm="0"
            class="d-flex align-center justify-center py-0">
            <v-divider inset vertical></v-divider>
          </v-col>
          <v-col cols="12" sm="5" class="pl-0 py-0">
            <PaymentMethodField
              :updatedSearchParams="updatedSearchParams.paymentMethods"
              @update:updatedSearchParams="newValues =>
                updatedSearchParams.paymentMethods = newValues"
            ></PaymentMethodField>
            <v-text-field
              v-model="updatedSearchParams.costCenter"
              label="Kustannuspaikka"
              clearable
              hide-details
            ></v-text-field>
            <EventTypeField
              :updatedSearchParams="updatedSearchParams.eventTypes"
              @update:updatedSearchParams="newValues =>
                updatedSearchParams.eventTypes = newValues"
            ></EventTypeField>
            <v-switch
              v-model="carPoolToggle"
              label="Yhdistelymatka"
              hide-details
            ></v-switch>
            <v-text-field
              v-model="updatedSearchParams.tripType"
              label="Matkan tyyppi"
              clearable
              hide-details
            ></v-text-field>
            <v-text-field
              v-model="updatedSearchParams.routeId"
              label="Reitin tunnus"
              clearable
              hide-details
            ></v-text-field>
          </v-col>
        </v-row>
        <v-row class="mt-2">
          <v-col cols="12" class="pl-0 py-0">
            <v-switch
              v-model="updatedSearchParams.removed"
              label="Etsi myös poistettuja tapahtumia"
            ></v-switch>
          </v-col>
        </v-row>
      </div>
    </v-expand-transition>
    </v-form>
</template>

<script>
import _ from "lodash";
import EventTypeField from "./SearchParamFields/EventTypeField";
import PaymentMethodField from "./SearchParamFields/PaymentMethodField";
import PurchaseInvoiceStatesField from "./SearchParamFields/PurchaseInvoiceStatesField";
import SalesInvoiceStatesField from "./SearchParamFields/SalesInvoiceStatesField";
import ProvisionInvoiceStatesField from "./SearchParamFields/ProvisionInvoiceStatesField";
import ValidationStatesField from "./SearchParamFields/ValidationStatesField";
import MeterTypesField from "./SearchParamFields/MeterTypesField";
import { TransportEventStatusMixin } from "@/models/TransportEventStatusTypes";
import { TranportEventValidationErrorMixin } from "@/models/ValidationErrors";
import { TransportEventInvoicingMixin } from "@/models/TransportEventInvoicingStates";
import { PaymentMethodMixin } from "@/models/PaymentMethods";
import { TransportEventTypeMixin } from "@/models/TransportEventTypes";
import { MeterTypeMixin } from "@/models/MeterTypes";
import { ProvisionInvoiceStateMixin } from "@/models/ProvisionInvoiceStates";

export default {
    props: {
        searchParams: Object
    },
    components: {
        EventTypeField,
        PaymentMethodField,
        PurchaseInvoiceStatesField,
        SalesInvoiceStatesField,
        ProvisionInvoiceStatesField,
        ValidationStatesField,
        MeterTypesField
    },
    mixins: [
        TransportEventStatusMixin,
        TranportEventValidationErrorMixin,
        TransportEventInvoicingMixin,
        PaymentMethodMixin,
        TransportEventTypeMixin,
        MeterTypeMixin,
        ProvisionInvoiceStateMixin
    ],
    data() {
        return {
            updatedSearchParams: this.formatSearchParams(this.searchParams),
            searchParamsValid: true,
            showMoreDateRanges: false,
            showInvoiceStates: false,
            showMoreFinancialRanges: false,
            showMoreSearchFilters: false
        };
    },
    watch: {
        searchParams(val) {
            this.updatedSearchParams = this.formatSearchParams(this.searchParams);
        }
    },
    computed: {
        hasFailedValidationsActive() {
            if (!this.updatedSearchParams.validationStates || this.updatedSearchParams.validationStates.length === 0) {
                return false;
            }
            // Check if all failed validation types are present in the current selection
            return this.validationStatusFailTypes.every(failType =>
                this.updatedSearchParams.validationStates.includes(failType)
            );
        },
        carPoolToggle: {
            get() {
                return this.updatedSearchParams.carPool === "K";
            },
            set(value) {
                this.updatedSearchParams.carPool = value ? "K" : "";
            }
        }
    },
    methods: {
        formatSearchParams(searchParams) {
            let params = {};
            if (!searchParams) return params;

            // Array fields (enum lists)
            params.validationStates = this.parseQueryParamsToIntList(searchParams.validationStates);
            params.salesInvoiceStates = this.parseQueryParamsToIntList(searchParams.salesInvoiceStates);
            params.purchaseInvoiceStates = this.parseQueryParamsToIntList(searchParams.purchaseInvoiceStates);
            params.provisionInvoiceStates = this.parseQueryParamsToIntList(searchParams.provisionInvoiceStates);
            params.paymentMethods = this.parseQueryParamsToIntList(searchParams.paymentMethods);
            params.eventTypes = this.parseQueryParamsToIntList(searchParams.eventTypes);
            params.meterTypes = this.parseQueryParamsToIntList(searchParams.meterTypes);

            // String array fields
            if (searchParams.validationFailReasonCodes) {
                params.validationFailReasonCodes = _.cloneDeep(searchParams.validationFailReasonCodes);
            }
            if (searchParams.salesInvoiceNumbers) {
                params.salesInvoiceNumbers = _.cloneDeep(searchParams.salesInvoiceNumbers);
            }
            if (searchParams.purchaseInvoiceNumbers) {
                params.purchaseInvoiceNumbers = _.cloneDeep(searchParams.purchaseInvoiceNumbers);
            }

            // String fields
            const stringFields = [
                "orderId", "operatorId", "routeId", "receiptNumber", "cardNumber",
                "costCenter", "carNumber", "serviceNumber", "taximeterSerialNumber",
                "carPool", "referenceNumber", "ssn", "tripType"
            ];
            stringFields.forEach(field => {
                if (searchParams[field]) {
                    params[field] = _.cloneDeep(searchParams[field]);
                }
            });

            // Date fields
            const dateFields = [
                "modifiedFrom", "modifiedTo", "tripStartedFrom", "tripStartedTo",
                "tripEndedFrom", "tripEndedTo"
            ];
            dateFields.forEach(field => {
                if (searchParams[field]) {
                    params[field] = _.cloneDeep(searchParams[field]);
                }
            });

            // Numeric fields
            const numericFields = [
                "costFrom", "costTo", "reimbursementFrom", "reimbursementTo",
                "deductibleFrom", "deductibleTo"
            ];
            numericFields.forEach(field => {
                if (searchParams[field]) {
                    params[field] = _.cloneDeep(searchParams[field]);
                }
            });

            // Boolean fields
            if (searchParams.removed) {
                params.removed = _.cloneDeep(searchParams.removed);
            }
            if (searchParams.missingTrip) {
                params.missingTrip = _.cloneDeep(searchParams.missingTrip);
            }
            if (searchParams.missingCharge) {
                params.missingCharge = _.cloneDeep(searchParams.missingCharge);
            }

            return params;
        },
        toggleFailedValidations() {
            if (this.hasFailedValidationsActive) {
                this.updatedSearchParams.validationStates = this.updatedSearchParams.validationStates.filter((el) => !this.validationStatusFailTypes.includes(el));
                return;
            }
            this.updatedSearchParams.validationStates = this.validationStatusFailTypes;
        },
        getSearchParameters() {
            // Clean up conditional fields
            if (this.updatedSearchParams.validationStates.length !== 1 &&
          !this.updatedSearchParams.validationStates.includes(this.validationStatusTypes.ExternalValidationFailed)) {
                delete this.updatedSearchParams.validationFailReasonCodes;
            }
            if (this.updatedSearchParams.validationStates.length !== 1 &&
          !this.updatedSearchParams.validationStates.includes(this.validationStatusTypes.New)) {
                delete this.updatedSearchParams.missingTrip;
                delete this.updatedSearchParams.missingCharge;
            }
            return this.updatedSearchParams;
        },
        reset() {
            this.updatedSearchParams = this.formatSearchParams({});
        },
        clearDateRange(type) {
            this.updatedSearchParams[`${type}From`] = null;
            this.updatedSearchParams[`${type}To`] = null;
        },
        clearFinancialRange(type) {
            this.updatedSearchParams[`${type}From`] = null;
            this.updatedSearchParams[`${type}To`] = null;
        },
        parseQueryParamsToIntList(params) {
            if (params) {
                let formatted;
                if (Array.isArray(params)) {
                    formatted = _.cloneDeep(params);
                } else {
                    formatted = [_.cloneDeep(params)];
                }
                formatted = formatted.map(x => parseInt(x, 10));
                return formatted;
            } else {
                return [];
            }
        }
    }
};
</script>

<style scoped>
.centered-input >>> input {
  text-align: center
}
</style>
